import { supabase } from '../../lib/supabase';
import { ProcessingProgress, ProcessingResult } from '../dataImportProcessor';

// Define batch size for large file processing
const BATCH_SIZE = 5000;

interface PatientData {
  patient_id: string;
  first_name: string;
  last_name: string;
  sex: string;
  date_of_birth: string;
}

interface AppointmentData {
  appointment_id: string;
  external_patient_id: string;
  appointment_date: string;
  appointment_time: string;
  appointment_type: string;
  rendering_provider: string;
  service_department: string;
  cancelled_date: string | null;
  notes: string | null;
}

export async function processAppointmentsReport(
  lines: string[],
  onProgress?: (progress: ProcessingProgress) => void
): Promise<ProcessingResult> {
  try {
    // Calculate total number of data lines (excluding headers)
    const totalDataLines = lines.length - 2;
    const headers = lines[1].split(',').map(header => header.trim().toLowerCase());
    let processedCount = 0;
    let skippedCount = 0;
    let errorCount = 0;

    // Calculate number of batches needed
    const numBatches = Math.ceil(totalDataLines / BATCH_SIZE);
    console.log(`Processing file with ${totalDataLines} lines in ${numBatches} batches of ${BATCH_SIZE} lines each`);

    let totalProcessedAppointments = 0;
    let totalSkippedAppointments = 0;

    // Process each batch
    for (let batchIndex = 0; batchIndex < numBatches; batchIndex++) {
      console.log(`Processing batch ${batchIndex + 1} of ${numBatches}`);
      
      // Calculate the start and end indices for this batch
      const startIdx = 2 + (batchIndex * BATCH_SIZE); // +2 to skip header rows
      const endIdx = Math.min(startIdx + BATCH_SIZE, lines.length);
      
      // Extract patient and appointment data for this batch
      const batchPatients: PatientData[] = [];
      const batchAppointments: AppointmentData[] = [];
      const patientIds = new Set<string>();
      const duplicatesInBatch = new Set<string>();

      // First pass: collect unique patients and appointments for this batch
      for (let i = startIdx; i < endIdx; i++) {
        const values = lines[i].split(',').map(value => value.trim());
        
        if (values.length !== headers.length) {
          skippedCount++;
          continue; // Skip malformed rows
        }

        const patientId = values[headers.indexOf('patientid')];
        if (!patientId) {
          skippedCount++;
          continue; // Skip rows without patient ID
        }

        // Look for the date of birth column - check for common column names
        const dobColumnNames = ['patientdob', 'date_of_birth', 'dob', 'patient_dob'];
        let dateOfBirth = null;
        
        for (const colName of dobColumnNames) {
          const colIndex = headers.indexOf(colName);
          if (colIndex !== -1 && values[colIndex]) {
            dateOfBirth = values[colIndex];
            break;
          }
        }

        // Skip rows without date of birth
        if (!dateOfBirth) {
          console.log(`Skipping row for patient ${patientId} - missing required date of birth`);
          skippedCount++;
          continue;
        }

        // Add appointment data
        const appointmentDate = values[headers.indexOf('apptdate')];
        const appointmentTime = values[headers.indexOf('apptstarttime')];
        const appointmentType = values[headers.indexOf('appttype')];
        const renderingProvider = values[headers.indexOf('rndrng prvdr')];
        const serviceDepart = values[headers.indexOf('svc dprtmnt')] || '';
        const cancelledDate = values[headers.indexOf('apptcancelleddate')] || null;
        const appointmentNote = values[headers.indexOf('apptnote')] || null;
        
        batchAppointments.push({
          appointment_id: values[headers.indexOf('apptid')],
          external_patient_id: patientId,
          appointment_date: appointmentDate,
          appointment_time: appointmentTime,
          appointment_type: appointmentType,
          rendering_provider: renderingProvider,
          service_department: serviceDepart,
          cancelled_date: cancelledDate,
          notes: appointmentNote
        });

        // Only add patient if not already seen in this batch
        if (!patientIds.has(patientId)) {
          patientIds.add(patientId);
          batchPatients.push({
            patient_id: patientId,
            first_name: values[headers.indexOf('patient firstname')],
            last_name: values[headers.indexOf('patient lastname')],
            sex: values[headers.indexOf('patientsex')],
            date_of_birth: dateOfBirth
          });
        } else {
          duplicatesInBatch.add(patientId);
        }

        // Report progress for data extraction
        processedCount++;
        if (onProgress) {
          onProgress({
            currentLine: startIdx + (i - startIdx),
            totalLines: totalDataLines,
            processedCount,
            skippedCount
          });
        }
      }

      console.log(`Batch ${batchIndex + 1}: Found ${batchPatients.length} patients and ${batchAppointments.length} appointments`);

      // Process patients in smaller batches
      const PATIENT_BATCH_SIZE = 50;
      const existingPatientMap = new Map<string, string>();

      for (let i = 0; i < batchPatients.length; i += PATIENT_BATCH_SIZE) {
        const patientBatch = batchPatients.slice(i, i + PATIENT_BATCH_SIZE);
        
        try {
          // Upsert patients with DOB
          const { error: upsertError } = await supabase
            .rpc('upsert_patients_with_dob', {
              p_patients: patientBatch.map(p => ({
                patient_id: p.patient_id,
                first_name: p.first_name,
                last_name: p.last_name,
                sex: p.sex,
                date_of_birth: p.date_of_birth
              }))
            });

          if (upsertError) {
            console.error('Error upserting patients:', upsertError);
            errorCount++;
            continue;
          }

          // Then get all patient IDs for this batch
          const { data: batchPatientData, error: selectError } = await supabase
            .from('patients')
            .select('id, patient_id')
            .in('patient_id', patientBatch.map(p => p.patient_id));

          if (selectError) {
            console.error('Error fetching patient IDs:', selectError);
            continue;
          }

          // Update the patient map
          batchPatientData?.forEach(p => {
            existingPatientMap.set(p.patient_id, p.id);
          });

        } catch (error: any) {
          console.error('Error processing patient batch:', error);
          errorCount++;
        }
      }

      // Process appointments in smaller batches
      let batchAppointmentsProcessed = 0;
      let batchAppointmentsSkipped = 0;

      // Get all existing appointments for these patients in one query
      const { data: existingAppointments, error: existingAppointmentsError } = await supabase
        .from('appointments')
        .select('appointment_id')
        .in('external_patient_id', Array.from(patientIds));

      // Create a Set of existing appointments for fast lookup
      const existingAppointmentsSet = new Set<string>();
      if (!existingAppointmentsError && existingAppointments) {
        existingAppointments.forEach(appointment => {
          existingAppointmentsSet.add(appointment.appointment_id);
        });
      }

      // Process appointments in smaller batches
      const APPOINTMENT_BATCH_SIZE = 100;
      for (let i = 0; i < batchAppointments.length; i += APPOINTMENT_BATCH_SIZE) {
        const appointmentBatch = batchAppointments.slice(i, i + APPOINTMENT_BATCH_SIZE);
        const appointmentsToInsert = appointmentBatch
          .map(appointment => ({
            ...appointment,
            patient_id: existingPatientMap.get(appointment.external_patient_id),
            appointment_date: new Date(appointment.appointment_date).toISOString().split('T')[0],
            cancelled_date: appointment.cancelled_date 
              ? new Date(appointment.cancelled_date).toISOString().split('T')[0]
              : null
          }))
          .filter(appointment => appointment.patient_id); // Only include appointments where we have a valid patient_id

        if (appointmentsToInsert.length > 0) {
          try {
            const { error: insertError } = await supabase
              .from('appointments')
              .upsert(appointmentsToInsert, { onConflict: 'appointment_id' });

            if (insertError) {
              console.error('Error inserting appointments:', insertError);
              batchAppointmentsSkipped += appointmentsToInsert.length;
            } else {
              batchAppointmentsProcessed += appointmentsToInsert.length;
            }
          } catch (error) {
            console.error('Error processing appointments batch:', error);
            batchAppointmentsSkipped += appointmentsToInsert.length;
          }
        } else {
          batchAppointmentsSkipped += appointmentBatch.length;
        }

        // Report progress
        if (onProgress) {
          onProgress({
            currentLine: processedCount,
            totalLines: totalDataLines,
            processedCount: totalProcessedAppointments + batchAppointmentsProcessed,
            skippedCount: totalSkippedAppointments + batchAppointmentsSkipped
          });
        }
      }

      // Add batch results to totals
      totalProcessedAppointments += batchAppointmentsProcessed;
      totalSkippedAppointments += batchAppointmentsSkipped;
      
      console.log(`Batch ${batchIndex + 1} complete: Processed ${batchAppointmentsProcessed}, Skipped ${batchAppointmentsSkipped}`);
    }

    // Record import history
    try {
      await supabase.from('import_history').insert({
        report_type: 'ACCESS_DATA_VISITS',
        file_name: 'ACCESS Data - WtgMgt VISITS',
        processed_count: totalProcessedAppointments,
        skipped_count: totalSkippedAppointments
      });
    } catch (error) {
      console.error('Error recording import history:', error);
    }

    let message = `Processed ${totalProcessedAppointments} new appointments`;
    if (totalSkippedAppointments > 0) {
      message += ` (${totalSkippedAppointments} appointments skipped)`;
    }
    if (numBatches > 1) {
      message += ` in ${numBatches} batches`;
    }
    if (errorCount > 0) {
      message += ` (${errorCount} errors occurred)`;
    }

    return {
      success: true,
      message,
      processedCount: totalProcessedAppointments,
      skippedCount: totalSkippedAppointments
    };
  } catch (error: any) {
    return {
      success: false,
      message: `Error processing file: ${error.message}`,
      processedCount: 0,
      skippedCount: 0
    };
  }
}